import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CompaniesService } from '../../../../services/companies.service';
import {
  EmployeeInfoAndStatusArgs,
  ReportDownloadArgs,
  ReportGeneratedArgs
} from '../../../../models/reports';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import {
  formatDatePickerValue,
  parseDatePickerValue
} from '../../../../utils/datepicker-value-parser';
import {
  dateRangeComponentValidator,
  dateRangeRequired,
  dateRangeValidator
} from '../../../../utils/dateRangeValidator';
import moment from 'moment';
import { EnumService } from '../../../../services/enum.service';

@Component({
  selector: 'lib-employee-info-and-status',
  templateUrl: './employee-info-and-status.component.html',
  styleUrls: ['./employee-info-and-status.component.css'],
  standalone: false
})
export class EmployeeInfoAndStatusComponent implements OnInit {
  @Input()
  public clientCode: string;
  @Output()
  public reportGenerated = new EventEmitter<ReportGeneratedArgs>();
  @Output()
  public reportDownloadClicked = new EventEmitter<ReportDownloadArgs>();
  public statuses: any[];
  private readonly ENDPOINT = 'EmployeeInfoAndStatus';
  public get setTimeframe() {
    return this.form.get('setTimeframe').value;
  }

  public form: UntypedFormGroup;
  private clientStartDate: Date;
  constructor(
    private fb: UntypedFormBuilder,
    private companyService: CompaniesService,
    private enumService: EnumService
  ) {}

  ngOnInit(): void {
    this.companyService.getCompanyFrontPage(this.clientCode).subscribe((x: any) => {
      this.clientStartDate = new Date(Date.parse(x.StartDate));
    });
    this.enumService.get('EmployeeStatuses').subscribe(statuses => {
      this.statuses = statuses;
      this._initForm();
    });
  }

  public generateReport() {
    const args = this._getModel();
    this.reportGenerated.emit({ args, endpoint: this.ENDPOINT });
  }

  public downloadReport(format: string) {
    const args = this._getModel();
    const formattedStartDate =
      args.StartDate != null ? `${moment(args.StartDate).format('MM-DD-YYYY')} to ` : '';
    const formattedEndDate = args.EndDate != null ? moment(args.EndDate).format('MM-DD-YYYY') : '';
    const now = moment().format('MMDDYYYY');
    this.reportDownloadClicked.emit({
      args,
      endpoint: this.ENDPOINT,
      filename: `Employee Info and Status_${this.clientCode}_${formattedStartDate}${formattedEndDate}_${now}.${format}`,
      format
    });
  }

  private _getModel() {
    const startDate = this.form.get('dateRange').value.start;
    const endDate = this.form.get('dateRange').value.end;
    const excludeDate = this.form.get('excludeEmployeesTerminatedPriorTo').value;
    const status = this.form.get('employeeStatus').value;
    const enableExcludeDate = this.form.get('enableExcludeDate').value;
    const model: EmployeeInfoAndStatusArgs = {
      EmployeeStatus: status != null && status != 'null' ? status : null,
      StartDate: startDate != null ? startDate : null,
      EndDate: endDate != null ? endDate : null,
      ExcludeEmployeesTerminatedPriorTo: enableExcludeDate
        ? parseDatePickerValue(excludeDate)
        : null
    };
    return model;
  }
  private _initForm() {
    const now = new Date();
    this.form = this.fb.group({
      employeeStatus: this.fb.control(null),
      dateRange: this.fb.control({ start: null, end: null }, []),
      enableExcludeDate: this.fb.control(false),
      excludeEmployeesTerminatedPriorTo: this.fb.control({
        value: formatDatePickerValue(new Date(now.getFullYear(), 0, 1)),
        disabled: true
      }),
      setTimeframe: this.fb.control(false)
    });
    this.form.get('setTimeframe').valueChanges.subscribe(val => {
      if (val) {
        this.form.get('dateRange').setValue({ start: this.clientStartDate, end: new Date() });
        this.form.get('dateRange').addValidators(dateRangeComponentValidator);
      } else {
        this.form.get('dateRange').setValue({ start: null, end: null });
        this.form.get('dateRange').removeValidators(dateRangeComponentValidator);
      }
      this.form.updateValueAndValidity();
    });
    this.form.get('enableExcludeDate').valueChanges.subscribe(val => {
      const control = this.form.get('excludeEmployeesTerminatedPriorTo');
      if (val) {
        control.enable();
      } else {
        control.disable();
      }
    });
  }
}
